import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CssVarsProvider } from '@mui/joy/styles';
import { buildJoyThemeFromTokens, applyThemeCssVariables } from './buildJoyTheme';
import { DEFAULT_UI_THEME, type JarvisUiThemeDefinition } from './tokens';
import { uiThemeService } from './uiTheme.service';
import { directSupabase } from '../lib/supabaseClient';

type JarvisThemeContextValue = {
  theme: JarvisUiThemeDefinition;
  themes: JarvisUiThemeDefinition[];
  loading: boolean;
  refresh: () => Promise<void>;
  activateTheme: (themeKey: string) => Promise<void>;
};

const JarvisThemeContext = createContext<JarvisThemeContextValue | null>(null);

export function useJarvisTheme(): JarvisThemeContextValue {
  const ctx = useContext(JarvisThemeContext);
  if (!ctx) {
    throw new Error('useJarvisTheme must be used within JarvisThemeProvider');
  }
  return ctx;
}

/** Optional hook that never throws outside the provider (for public routes). */
export function useJarvisThemeOptional(): JarvisThemeContextValue | null {
  return useContext(JarvisThemeContext);
}

export function JarvisThemeProvider({ children }: { children: React.ReactNode }) {
  const [themes, setThemes] = useState<JarvisUiThemeDefinition[]>([DEFAULT_UI_THEME]);
  const [theme, setTheme] = useState<JarvisUiThemeDefinition>(DEFAULT_UI_THEME);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [list, active] = await Promise.all([
        uiThemeService.listThemes(),
        uiThemeService.resolveActiveTheme(),
      ]);
      setThemes(list);
      applyThemeCssVariables(active.tokens);
      setTheme((prev) => (
        prev.key === active.key && prev.updatedAt === active.updatedAt ? prev : active
      ));
    } catch (err) {
      console.warn('Failed to load JARVIS UI themes; using Default', err);
      setThemes([DEFAULT_UI_THEME]);
      setTheme(DEFAULT_UI_THEME);
      applyThemeCssVariables(DEFAULT_UI_THEME.tokens);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Seed CSS vars before the async load so nav chrome is never unstyled.
    applyThemeCssVariables(DEFAULT_UI_THEME.tokens);
    void refresh();
    // Theme provider sits above the auth tree, so the first read can be anonymous.
    // Reload once a session exists — Job Records (fitter/labourer) always lands after login.
    const { data } = directSupabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
        void refresh();
      }
      if (event === 'SIGNED_OUT') {
        applyThemeCssVariables(DEFAULT_UI_THEME.tokens);
      }
    });
    return () => {
      data.subscription.unsubscribe();
    };
  }, [refresh]);

  const activateTheme = useCallback(async (themeKey: string) => {
    await uiThemeService.setActiveThemeKey(themeKey);
    await refresh();
  }, [refresh]);

  const joyTheme = useMemo(
    () => buildJoyThemeFromTokens(theme.tokens),
    [theme],
  );

  const value = useMemo<JarvisThemeContextValue>(() => ({
    theme,
    themes,
    loading,
    refresh,
    activateTheme,
  }), [theme, themes, loading, refresh, activateTheme]);

  return (
    <JarvisThemeContext.Provider value={value}>
      <CssVarsProvider theme={joyTheme} defaultMode="light">
        {children}
      </CssVarsProvider>
    </JarvisThemeContext.Provider>
  );
}
